import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="facility-wizard"
export default class extends Controller {
  static targets = [
    "countrySelect",
    "regionSelect",
    "nextButton",
    "facilityNameInput",
    "currencySelect",
    "sectorSelect",
  ];

  initialize() {
    this.pageLoadHandler = this.handlePageLoad.bind(this);
    this.frameLoadHandler = this.handleFrameLoad.bind(this);
    this.productIdsChangedHandler = this.handleProductIdsChanged.bind(this);
  }

  connect() {
    // Initial validation check for any step
    this.validateForm();

    // Update progress bar on connect (when frame loads)
    this.updateProgressFromUrl();
    this.forceProgressUpdate();

    // Listen for Turbo navigation events to update progress bar
    document.addEventListener("turbo:load", this.pageLoadHandler);

    document.addEventListener("turbo:frame-load", this.frameLoadHandler);

    // Listen for product IDs changes from sector-products controller
    document.addEventListener(
      "productIdsChanged",
      this.productIdsChangedHandler
    );
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.pageLoadHandler);

    document.removeEventListener("turbo:frame-load", this.frameLoadHandler);

    document.removeEventListener(
      "productIdsChanged",
      this.productIdsChangedHandler
    );
  }

  handlePageLoad() {
    // Update progress bar on full page navigation
    this.updateProgressFromUrl();
  }

  handleFrameLoad(event) {
    // Check if this is the wizard content frame
    if (event.target.id === "wizard_content") {
      // Update progress bar on frame navigation
      this.updateProgressFromUrl();

      // Validate form after frame loads
      this.validateForm();
    }
  }

  handleProductIdsChanged() {
    // Triggered when product IDs are changed in the sector-products controller
    this.validateForm();
  }

  confirmLeave(event) {
    const message =
      "Are you sure you want to leave? This facility hasn't been saved yet.";

    if (!window.confirm(message)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  updateProgressFromUrl() {
    // First try to get step number from progress bar container data attributes
    const progressContainer = document.getElementById("wizard-progress-bar");

    if (progressContainer) {
      const stepNumber = progressContainer.dataset.wizardStep;

      if (stepNumber && window.updateWizardProgress) {
        window.updateWizardProgress(parseInt(stepNumber));
        return;
      }
    }

    // Fallback: try wizard frame
    const wizardFrame = document.getElementById("wizard_content");

    if (wizardFrame) {
      const stepNumber = wizardFrame.dataset.wizardStep;

      if (stepNumber && window.updateWizardProgress) {
        window.updateWizardProgress(parseInt(stepNumber));
        return;
      }
    }

    // Fallback: Extract step number from current URL
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split("/");
    const stepIndex = pathParts.indexOf("wizard");

    if (stepIndex !== -1 && pathParts[stepIndex + 1]) {
      const stepName = pathParts[stepIndex + 1];
      const steps = [
        "location_information",
        "facility_information",
        "sector_products",
        "overview",
      ];
      const stepNumber = steps.indexOf(stepName) + 1;

      if (stepNumber > 0 && window.updateWizardProgress) {
        window.updateWizardProgress(stepNumber);
      }
    }
  }

  // Force progress bar update when this controller connects
  forceProgressUpdate() {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      this.updateProgressFromUrl();
    }, 100);
  }

  updateRegions(preserveSelection = false) {
    const countryCode = this.countrySelectTarget.value;
    const regionSelect = this.regionSelectTarget;
    const currentRegionValue = preserveSelection ? regionSelect.value : "";

    // If regions are already populated and we have a current value, don't reload (only if preserving selection)
    if (
      preserveSelection &&
      regionSelect.options.length > 1 &&
      currentRegionValue
    ) {
      this.validateForm();
      return;
    }

    // Clear existing options
    regionSelect.innerHTML = "";

    if (!countryCode) {
      // No country selected
      regionSelect.disabled = true;
      regionSelect.classList.add("bg-gray-50");
      regionSelect.innerHTML = '<option value="">Select country first</option>';
      this.validateForm();
      return;
    }

    // Enable the region select
    regionSelect.disabled = false;
    regionSelect.classList.remove("bg-gray-50");
    regionSelect.innerHTML = '<option value="">Loading regions...</option>';

    // Fetch regions for the selected country using the wizard endpoint
    const currentLocale = window.location.pathname.split("/")[1]; // Extract locale from URL
    const url = `/${currentLocale}/facilities/wizard/api/countries/${countryCode}/regions`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        regionSelect.innerHTML = '<option value="">Select region</option>';

        if (data.regions && data.regions.length > 0) {
          data.regions.forEach((region) => {
            const option = document.createElement("option");
            option.value = region.code;
            option.textContent = region.name;

            // Only restore previously selected value if preserveSelection is true
            if (preserveSelection && region.code === currentRegionValue) {
              option.selected = true;
            }

            regionSelect.appendChild(option);
          });
        } else {
          // When no regions are available, provide "No region" as an option
          regionSelect.innerHTML = '<option value="">Select region</option>';
          const noRegionOption = document.createElement("option");
          noRegionOption.value = "No region";
          noRegionOption.textContent = "No region";

          // Auto-select if it was previously selected
          if (preserveSelection && currentRegionValue === "No region") {
            noRegionOption.selected = true;
          }

          regionSelect.appendChild(noRegionOption);
        }

        // Validate form after regions are loaded
        this.validateForm();
      })
      .catch((error) => {
        console.error("Error loading regions:", error);
        regionSelect.innerHTML =
          '<option value="">Error loading regions</option>';
        // Keep the select enabled even on error
        regionSelect.classList.remove("bg-gray-50");
        this.validateForm();
      });
  }

  // Handle user-initiated country changes
  countryChanged() {
    // This is called when user actively changes the country dropdown
    // Always update regions in this case since it's a user action
    if (this.hasCountrySelectTarget && this.hasRegionSelectTarget) {
      // Clear the region value to prevent country/region mismatches
      this.regionSelectTarget.value = "";
      // Call updateRegions without preserving selection to ensure region is cleared
      this.updateRegions(false);
    }
  }

  // Add form validation method
  validateForm() {
    if (!this.hasNextButtonTarget) return;

    let isValid = false;

    // Check if we're on the location information step
    if (this.hasCountrySelectTarget && this.hasRegionSelectTarget) {
      const countryValue = this.countrySelectTarget.value;
      const regionValue = this.regionSelectTarget.value;
      isValid = countryValue && regionValue;
    }
    // Check if we're on the facility information step
    else if (this.hasFacilityNameInputTarget && this.hasCurrencySelectTarget) {
      const nameValue = this.facilityNameInputTarget.value.trim();
      const currencyValue = this.currencySelectTarget.value;
      isValid = nameValue && currencyValue;
    }
    // Check if we're on the sector products step
    else if (this.hasSectorSelectTarget) {
      const sectorValue = this.sectorSelectTarget.value;

      // Check if products are selected (look for the hidden field)
      const productIdsField = document.querySelector(
        '[data-sector-products-target="productIdsField"]'
      );
      const hasProducts =
        productIdsField && productIdsField.value.trim() !== "";

      isValid = sectorValue && hasProducts;
    }
    // For overview step, always enable the button
    else {
      isValid = true;
    }

    if (isValid) {
      this.enableNextButton();
    } else {
      this.disableNextButton();
    }
  }

  // Enable the next button
  enableNextButton() {
    this.nextButtonTarget.disabled = false;
    // The form-button-primary class already handles the enabled state
  }

  // Disable the next button
  disableNextButton() {
    this.nextButtonTarget.disabled = true;
    // The form-button-primary:disabled class already handles the disabled state
  }

  // Add event handler for region changes
  regionChanged() {
    this.validateForm();
  }

  // Add event handlers for facility information form fields
  facilityFieldChanged() {
    this.validateForm();
  }
}
